import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { DocumentsService } from '@app/core/services';
import { IAGProductLineTableConfig } from '@app/products/dmepos/models/ag-product-line-table-config.model';
import { TableColumnConfig } from '@app/shared/models';
import { LibPipeModule } from '@mynexus/mynexus-ui-lib';
import { SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Table, TableModule } from 'primeng/table';

@Component({
    selector: 'mnc-product-lines-selection',
    templateUrl: './product-lines-selection.component.html',
    imports: [
        CommonModule,
        LibPipeModule,
        MultiSelectModule,
        TableModule,
        InputTextModule,
        ButtonModule,
    ]
})
export class ProductLinesSelectionComponent implements OnInit, OnChanges {
  @Input() filterable!: boolean;
  @Input() lines!: object[];
  @Input() sortable!: boolean;
  @Output() linesSelected = new EventEmitter<string[]>();

  @ViewChild('dataTable', { static: true }) dataTable!: Table;

  tableData: IAGProductLineTableConfig[];
  columnConfig: TableColumnConfig[] = [];
  selection: object;

  private readonly documentsService = inject(DocumentsService);

  ngOnInit(): void {
    this.tableData = this.initTableData();
    this.columnConfig = this.initTableConfig();
  }

 // Need to keep data from the product line widget in sync with this component
  ngOnChanges(changes: SimpleChanges): void {
    const lines = changes['lines']?.currentValue;

    if (lines) {
      this.lines = lines;
      this.tableData = this.initTableData();
    }
  }

  selectionChange(data: IAGProductLineTableConfig[]) {
    this.linesSelected.emit(data.map((x) => x.productLineId));
  }

  clearLineTableFilters(): void {
    this.dataTable.clear();
    this.columnConfig = this.initTableConfig();
  }

  private initTableData(): IAGProductLineTableConfig[] {
    return this.lines.map((x) => ({
      productLineId: x['productLineId'],
      lineNumber: x['sequenceNumber'],
      requestReceivedDate: x['requestReceivedDate'],
      requestType: x['requestType']['description'],
      urgency: x['urgency']['description'],
      hcpcs: this.getHcpcs(x['hcpcsByModifier']),
      status: x['status']['description'],
      statusReason: x['statusReason']['description'],
      unitsRequested: x['unitsRequested'] || '-',
      unitsApproved: x['unitsApproved'] || '-',
      unitsDenied: x['unitsDenied'] || '-',
      effectiveStartDate: x['effectiveStartDate'],
      effectiveEndDate: x['effectiveEndDate'],
      createdBy: x['createdBy'],
    }));
  }

  private initTableConfig(): TableColumnConfig[] {
    let lineNumberList: SelectItem[] = [];
    let requestTypeList: SelectItem[] = [];
    let statusList: SelectItem[] = [];

    if (this.filterable) {
      lineNumberList = this.documentsService
        .getUniqueFilterOptions(this.tableData.map((x) => x.lineNumber));

      requestTypeList = this.documentsService
        .getUniqueFilterOptions(this.tableData.map((x) => x.requestType));

      statusList = this.documentsService
        .getUniqueFilterOptions(this.tableData.map((x) => x.status));
    }

    return [
      {
        header: 'Product Line Number',
        field: 'lineNumber',
        styles: { minWidth: '125px' },
        isSortable: false,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: lineNumberList,
      },
      {
        header: 'Date/Time Request Received',
        field: 'requestReceivedDate',
        styles: { minWidth: '180px' },
        isSortable: this.sortable,
      },
      {
        header: 'Request Type',
        field: 'requestType',
        styles: { minWidth: '140px' },
        isSortable: false,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: requestTypeList,
      },
      {
        header: 'Urgency',
        field: 'urgency',
        styles: { minWidth: '140px' },
        isSortable: false,
      },
      {
        header: 'HCPCS + Modifier',
        field: 'hcpcs',
        styles: { minWidth: '150px' },
        isSortable: false,
      },
      {
        header: 'Status',
        field: 'status',
        styles: { minWidth: '150px' },
        isSortable: false,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: statusList,
      },
      {
        header: 'Status Reason',
        field: 'statusReason',
        styles: { minWidth: '150px' },
        isSortable: false,
      },
      {
        header: 'Requested Units',
        field: 'unitsRequested',
        styles: { minWidth: '105px' },
        isSortable: false,
      },
      {
        header: 'Approved Units',
        field: 'unitsApproved',
        styles: { minWidth: '105px' },
        isSortable: false,
      },
      {
        header: 'Denied Units',
        field: 'unitsDenied',
        styles: { minWidth: '105px' },
        isSortable: false,
      },
      {
        header: 'Effective From',
        field: 'effectiveStartDate',
        styles: { minWidth: '130px' },
        isSortable: false,
      },
      {
        header: 'Effective To',
        field: 'effectiveEndDate',
        styles: { minWidth: '130px' },
        isSortable: false,
      },
      {
        header: 'Created By',
        field: 'createdBy',
        styles: { minWidth: '150px' },
        isSortable: false,
      },
    ];
  }

  private getHcpcs(hcpcsByModifier: object): string {
    if (!hcpcsByModifier) return '-';

    const code = hcpcsByModifier['hcpcs']['code'];
    const modifier = hcpcsByModifier['hcpcsModifier']?.description || '';

    return `${code}${modifier}`;
  }
}
